import { html, LitElement, PropertyValues, TemplateResult } from 'lit';
import { styles } from '../view/styles';
import { GzWizardWebComponent } from '../../../ui/gzWizard';
import { GzRuleDefinitionWebComponent } from './ruleDefinition';
import { GzRuleSettingsWebComponent } from './ruleSettings';
import { translateFn } from '../../../utils/translator.helper';
import { DetectionRuleType, GridType, RuleType, Status } from '../../customRulesGrid/types';
import { GzYaraDefinitionWebComponent } from './yaraDefinition';
import {
  CHANGE_TO_ADVANCED,
  CHANGE_TO_BASIC,
  SWITCH_TO_ADVANCED,
  SWITCH_TO_BASIC, TARGET_CHANGE,
  WIZARD_STEP_CHANGE,
} from '../constants';
import { WizardEvent } from '../../../ui/gzWizard/types';
import { RuleTargetsWebComponent } from './ruleTargets';
import { ConsoleRequest, DataProviderType } from '../../../utils/request';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';
import { GzToast, ToastSeverity } from '../../../ui/gzToastNotification/type';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { SpinnerWidth } from '../../../ui/gzProgressSpinner/types';
import { customElement, property, state } from 'lit/decorators.js';
import { GzRuleAutomaticActionsWebComponent } from './ruleAutomaticActions';
import { Events } from './ruleAutomaticActions/types';
import { Feature } from '@nirvana/shared/constants';

@customElement('gz-add-rule')
export class GzAddRuleWebComponent extends LitElement {
  @property({ type: Boolean, attribute: false })
  public hasAccessOnCompany: boolean = false;

  @property({ type: Boolean, attribute: false })
  public hasEditInfoStep3: boolean = false;

  @property({ type: Number, attribute: true })
  public wizardStep: number;

  @property({ type: Object, attribute: false, reflect: false })
  oldRule: any = null;

  @property({ type: Object, attribute: false, reflect: false })
  rule: any = null;

  @property({ type: Array, attribute: false, reflect: false })
  availableCompanies;

  @property({ type: Array, attribute: false, reflect: false })
  availableCompaniesWithEdr;

  @property({ type: String, attribute: false, reflect: false })
  gridType;

  @property({ type: Array, attribute: false, reflect: false })
  tags;

  @property({ type: Array, attribute: false, reflect: false })
  availableEndpointTags;

  @property({ type: Number, attribute: true })
  type = RuleType.BASIC;

  @property({ type: Boolean })
  yaraValid = false;

  @property({ type: Boolean })
  isPartnerCompany = false;

  @property({ type: Boolean })
  isGZ = false;

  @property({ type: Object, attribute: false, reflect: false })
  public company: any;

  @property({ type: Boolean, attribute: false, reflect: false })
  public isEdrStandalone: boolean;

  @property({ type: Function, attribute: false, reflect: false })
  public platformHasFeature: (feature: Feature) => boolean;

  @property({ type: Array, attribute: false, reflect: false })
  public userCompany;

  @state()
  protected isLoading = false;

  public ruleDefinition: GzRuleDefinitionWebComponent | GzYaraDefinitionWebComponent;
  public ruleSettings: GzRuleSettingsWebComponent;
  public ruleTargets: RuleTargetsWebComponent;
  public ruleAutomaticActions: GzRuleAutomaticActionsWebComponent;
  public wizard: GzWizardWebComponent;

  static get styles() {
    return styles;
  }

  renderRuleDefinition() {
    this.ruleDefinition = new GzRuleDefinitionWebComponent();
    this.ruleDefinition.gridType = this.gridType;
    this.ruleDefinition.rule = this.rule;
    this.ruleDefinition.isGZ = this.isGZ;
    this.ruleDefinition.addEventListener(CHANGE_TO_ADVANCED, async () => {
      this.isLoading = true;

      try {
        const result = await ConsoleRequest.createRequest<any>({
          dataProviderType: DataProviderType.CORE,
          endpoint: 'DetectionRules',
          method: 'checkYaraRulesLimit',
          payload: {},
        });

        if (result) {
          this.rule.type = RuleType.YARA;
          this.rule.query = {
            yara: '',
          };
          this.rule.status = Status.DISABLED;
          this.rule.generateRca = true;
          this.dispatchEvent(
            new CustomEvent(SWITCH_TO_ADVANCED, {
              bubbles: true,
              cancelable: true,
              composed: true,
            })
          );
          this.requestUpdate('rule');
          return;
        }

        this.createLimitReachedDialog();
      } catch (error) {
        console.error(error);
        const toastNotificationConfigs: GzToast = {
          message: translateFn('Error retrieving the YARA rules limit.'),
          hasAction: false,
          severity: ToastSeverity.ERROR,
          keepOpen: false,
          autoCloseDelayMs: 5000,
        };

        GzToastNotificationWebComponent.buildToastWrapperElement(toastNotificationConfigs);
      } finally {
        this.isLoading = false;
      }
    });

    return html`${this.ruleDefinition}`;
  }

  renderYaraDefinition() {
    this.ruleDefinition = new GzYaraDefinitionWebComponent();
    this.ruleDefinition.addEventListener(CHANGE_TO_BASIC, () => {
      this.rule.type = RuleType.BASIC;
      this.rule.query = {
        type: DetectionRuleType.PROCESS,
        rules: [
          {
            criteriaValue: null,
            operatorValue: null,
            inputValue: null,
          },
        ],
        status: Status.ENABLED,
      };
      this.dispatchEvent(
        new CustomEvent(SWITCH_TO_BASIC, {
          bubbles: true,
          cancelable: true,
          composed: true,
        })
      );
      this.requestUpdate('rule');
    });
    this.ruleDefinition.rule = this.rule;

    return html`${this.ruleDefinition}`;
  }

  render() {
    return html`
      <div class="overlay ${this.isLoading ? '' : 'hidden'}" id="loading-overlay">
        <gz-progress-spinner spinnerWidth="${SpinnerWidth.LARGE}"></gz-progress-spinner>
      </div>
      <div class="d-flex mb-2">${this.renderWizard()}</div>
      <div class="d-flex ml-5 mb-2">${this.renderWizardStep()}</div>
    `;
  }

  protected updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);
    this.updateComplete.then(() => {
      for (let i = 0; i < this.wizardStep - 1; i++) {
        this.wizard.completeStep(i);
      }
    });
  }

  private renderWizard(): TemplateResult {
    this.wizard = new GzWizardWebComponent();

    this.wizard.addEventListener(WizardEvent.SELECTION_CHANGE, (event: CustomEvent): void => {
      this.setStep(event.detail.selectedIndex + 1);
    });
    this.wizard.activeIndex = this.wizardStep - 1;

    const step2Text = translateFn('Specify rule details and what should happen when this behavior is identified.');

    if (this.gridType === GridType.DETECTION) {
      const options = [
        {
          step: 1,
          title: translateFn('Detection rule definition'),
          message: translateFn(
            "Define rules to mark a specific behavior as a valid detection. Avoid creating generic rules, to prevent overloading your security team's backlog with false-positive incidents."
          ),
        },
        {
          step: 2,
          title: translateFn('Detection rule settings'),
          message: step2Text,
        },
        {
          step: 3,
          title: translateFn('Detection rule targets'),
          message: translateFn('Specify which devices should be scanned using the newly created rule.'),
        },
      ];

      if (RuleType.BASIC === this.rule.type
        && !this.isEdrStandalone
        && this.platformHasFeature(Feature.PLATFORM_FEATURE_EDR_AUTOMATIC_ACTIONS)
      ) {
        options.push({
          step: 4,
          title: translateFn('Automatic actions'),
          message: translateFn('Enable specific automatic response actions for triggered rules.'),
        });
      }

      this.wizard.options = options;
    } else {
      this.wizard.options = [
        {
          step: 1,
          title: translateFn('Exclusion rule definition'),
          message: translateFn(
            'Define rules to exclude specific behavior that may trigger false-positive alerts. Avoid using generic rules, to prevent valid incidents from being generated.'
          ),
        },
        {
          step: 2,
          title: translateFn('Exclusion rule settings'),
          message: step2Text,
        },
        {
          step: 3,
          title: translateFn('Exclusion rule targets'),
          message: translateFn('Specify which devices should be scanned using the newly created rule.'),
        },
      ];
    }

    return html`${this.wizard} `;
  }

  private setStep(step: number): void {
    this.wizardStep = step;

    this.dispatchEvent(
      new CustomEvent(WIZARD_STEP_CHANGE, {
        detail: {
          step: this.wizardStep,
        },
        bubbles: true,
        cancelable: true,
        composed: true,
      })
    );
  }

  private renderWizardStep(): TemplateResult {
    switch (this.wizardStep) {
      case 2:
        this.ruleSettings = new GzRuleSettingsWebComponent();
        this.ruleSettings.rule = this.rule;
        this.ruleSettings.gridType = this.gridType;
        this.ruleSettings.tags = this.tags;
        this.ruleSettings.isGZ = this.isGZ;
        this.ruleSettings.rule = this.rule;
        this.ruleSettings.isEdrStandalone = this.isEdrStandalone;
        this.ruleSettings.areAutomaticActionsAvailable = this.platformHasFeature(
          Feature.PLATFORM_FEATURE_EDR_AUTOMATIC_ACTIONS
        );

        return html`${this.ruleSettings}`;

      case 3:
        this.ruleTargets = new RuleTargetsWebComponent();
        this.ruleTargets.rule = this.rule;
        this.ruleTargets.company = (this.rule  && this.rule.companyId !== this.company.companyId)
          ? {
              companyId: this.rule.companyId,
              companyName: this.availableCompanies.filter(company => company._id === this.rule.companyId)[0].name,
            }
          : this.company;
        this.ruleTargets.availableEndpointTags = this.rule.type === RuleType.YARA
          ? (this.availableEndpointTags ?? []).filter(tag => tag.yaraEligibleTag)
          : this.availableEndpointTags;
        this.ruleTargets.isPartnerCompany = this.isPartnerCompany;
        this.ruleTargets.hasAccessOnCompany = this.hasAccessOnCompany;
        this.ruleTargets.hasEditInfoStep3 = this.hasEditInfoStep3;
        this.ruleTargets.isGZ = this.isGZ;
        this.ruleTargets.gridType = this.gridType;
        this.ruleTargets.availableCompanies = this.availableCompanies;
        this.ruleTargets.availableCompaniesWithEdr = this.availableCompaniesWithEdr;
        this.ruleTargets.userCompany = this.userCompany;

        this.ruleTargets.addEventListener(TARGET_CHANGE, () => {
          this.dispatchEvent(new CustomEvent(TARGET_CHANGE, {
            detail: {
              targets: this.rule.targets,
            }
          }));
        });

        return html`${this.ruleTargets}`;

      case 4:
        this.ruleAutomaticActions = new GzRuleAutomaticActionsWebComponent();

        this.ruleAutomaticActions.rule = this.rule;
        this.ruleAutomaticActions.isGZ = this.isGZ;
        this.ruleAutomaticActions.gridType = this.gridType;
        this.ruleAutomaticActions.platformHasFeature = this.platformHasFeature;

        this.ruleAutomaticActions.addEventListener(Events.GO_TO_STEP2, (): void => {
          this.setStep(2);
        });

        return html`${this.ruleAutomaticActions}`;

      case 1:
      default:
        if (this.rule.type === RuleType.YARA) {
          return this.renderYaraDefinition();
        } else {
          return this.renderRuleDefinition();
        }
    }
  }

  public createLimitReachedDialog() {
    const dialogBody = document.createElement('div');
    dialogBody.innerHTML =
      '<p>You have reached the maximum number of YARA rules allowed. Either delete existing YARA rules or create basic rules instead.</p>';

    GzDialogWebComponent.computeCustomDialog(
      dialogBody,
      `<span class="w-full d-flex justify-end">
            <gz-button id="cancel" type="outline" color="secondary">${translateFn('Cancel')}</gz-button>
        </span>`,
      translateFn('Maximum limit for YARA rules')
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-add-rule': GzAddRuleWebComponent;
  }
}
